// 검차 목록 카드 — 시안 A2 `.rw`. 결정 대기(completed)만 앰버 tint.
// 제목·부제 폴백은 fmt.js(orderTitle/orderSubtitle) — 링크 신청 / 스크린샷 신청도 사람이 읽는 한 줄이 된다.
// props: { order, wide, variant, selected, onClick }
//   variant='pane' = 데스크톱 2-pane 좌 목록의 밀도 행(시안 C1 `.rw` — 글리프 40 · 2줄 · 선택 accent 바).
import React from 'react';
import { T, R, FONT } from '../../../theme';
import { useT } from '../../../../i18n/I18nProvider';
import { orderTitle, orderSubtitle, fmtDate, listingHost } from '../../fmt';
import LtrText from '../../parts/LtrText';
import SimpleStatusPill from '../../parts/SimpleStatusPill';
import { CancelReasonBadge, ReapplyLink } from '../../parts/CancelReasonBits';

// 신청 종류 글리프 — 차량 / 링크 / 스크린샷 (이모지 금지, 인라인 SVG).
var PATHS = {
  car: 'M3 13l1.6-4.4A2 2 0 016.5 7h7.3a2 2 0 011.9 1.3L17 13M3 13h14v4H3v-4zm2.5 4v1.6M14.5 17v1.6',
  link: 'M10.3 13.7a3.8 3.8 0 005.7.4l2.6-2.6a3.8 3.8 0 00-5.4-5.4l-1.4 1.4M13.7 10.3a3.8 3.8 0 00-5.7-.4l-2.6 2.6a3.8 3.8 0 005.4 5.4l1.4-1.4',
  photo: 'M3 5h18v14H3V5zm1 12l4.6-4.2L13 17l3-2.6 4 3.4'
};

var rowBase = {
  display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', width: '100%', minWidth: 0,
  border: 0, borderBottom: '1px solid ' + T.bdSoft, background: T.card, cursor: 'pointer',
  fontFamily: 'inherit', textAlign: 'start', boxSizing: 'border-box'
};
// 2-pane 좌 목록 행 — 선택/앰버 변형은 rowBase 와 **키 집합이 같아야** 한다(v2 theme.js 규칙: 단축만 교체).
var paneRowBase = Object.assign({}, rowBase, { gap: 12, padding: '9px 14px', minHeight: 52,
  borderInlineStart: '3px solid transparent' });
var paneRowAmber = Object.assign({}, paneRowBase, { background: T.warnTint });
var paneRowSel = Object.assign({}, paneRowBase, { background: T.accentTint, borderInlineStart: '3px solid ' + T.accent });
var tile = { width: 34, height: 34, borderRadius: R.md, display: 'grid', placeItems: 'center', flex: 'none' };
var tileLg = Object.assign({}, tile, { width: 40, height: 40 });
var txWrap = { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 };
var titleStyle = { fontSize: 13, fontWeight: 700, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var subStyle = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: T.muted, minWidth: 0, flexWrap: 'wrap' };
var metaStyle = { display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: T.muted, flex: 'none' };
var metaCol = { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 };
var metaVal = { fontSize: 11.5, fontWeight: 700, color: T.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var tagStyle = { fontSize: FONT.badge, fontWeight: 700, color: T.accentPress, background: T.accentTint,
  border: '1px solid ' + T.accentBd, borderRadius: R.pill, padding: '1px 7px' };

function iconOf(order) {
  if (order && order.car_model) return 'car';
  if (order && order.listing_url) return 'link';
  return 'photo';
}

function RequestCard(props) {
  var t = useT();
  var o = props.order || {};
  var amber = o.status === 'completed';
  var pane = props.variant === 'pane';
  var kind = iconOf(o);
  var sub = orderSubtitle(t, o);
  var tint = amber ? { background: T.okTint, color: T.ok } : { background: T.track, color: T.muted };
  var style = pane
    ? (props.selected ? paneRowSel : amber ? paneRowAmber : paneRowBase)
    : (amber ? Object.assign({}, rowBase, { background: T.warnTint }) : rowBase);
  return (<button type="button" className="ss-rw" data-order-id={o.id} aria-current={pane && props.selected ? 'true' : undefined}
    style={style} onClick={props.onClick}>
    <span style={Object.assign({}, pane ? tileLg : tile, tint)}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d={PATHS[kind]} />
        {kind === 'car' ? <circle cx="18" cy="8.5" r="3.6" /> : null}
        {kind === 'photo' ? <circle cx="8.6" cy="10" r="1.7" /> : null}
      </svg>
    </span>
    <span style={txWrap}>
      <span style={titleStyle}>{orderTitle(t, o)}</span>
      {pane ? (<span style={subStyle}>
        {sub ? <span>{sub}</span> : (o.car_number ? <LtrText>{o.car_number}</LtrText> : null)}
        {o.inspector_name ? <span>{o.inspector_name}</span> : null}
        <LtrText>{fmtDate(null, o.inspect_date || o.created_at)}</LtrText>
      </span>) : (<span style={subStyle}>
        {sub ? <span>{sub}</span> : null}
        {o.car_number ? <LtrText>{o.car_number}</LtrText> : null}
        {o.car_year ? <LtrText>{o.car_year}</LtrText> : null}
        {!props.wide ? <LtrText>{fmtDate(null, o.created_at)}</LtrText> : null}
        {o.listing_url && o.car_model ? <span style={tagStyle}>{t('inspListingTitle')}</span> : null}
        {Array.isArray(o.request_photos) && o.request_photos.length
          ? <span style={tagStyle}>{t('inspPhotoCount', { count: o.request_photos.length })}</span> : null}
      </span>)}
      <ReapplyLink order={o} />
    </span>
    {props.wide ? (<span style={metaStyle}>
      <span style={metaCol}>
        <span>{t('evalTableColInspector')}</span>
        <span style={metaVal}>{o.inspector_name || '-'}</span>
      </span>
      <span style={metaCol}>
        <span>{t('evalLocationLabel')}</span>
        <span style={metaVal}>{o.location || (o.listing_url ? listingHost(o) : '-')}</span>
      </span>
      <span style={metaCol}>
        <span>{t('evalTableColDate')}</span>
        <LtrText style={metaVal}>{fmtDate(null, o.inspect_date || o.created_at)}</LtrText>
      </span>
    </span>) : null}
    <CancelReasonBadge order={o} />
    <SimpleStatusPill status={o.status} />
  </button>);
}

export default RequestCard;
